import { useCallback, useEffect, useState } from 'react'

import { UseDropdownProps } from './Filters.types'

export const useDropdowns = (): UseDropdownProps => {
	const [activeDropdown, setActiveDropdown] = useState(null)

	const positionPanel = (attribute: string): void => {
		const dropdown = document.querySelector(
			`[data-dropdown="${attribute}"]`
		) as HTMLElement

		if (!dropdown) {
			return
		}

		const threshold = window.innerWidth - 32

		const dropdownEdge = dropdown.getBoundingClientRect()?.right

		const isOffscreen = dropdownEdge > threshold

		if (!isOffscreen) {
			return
		}

		const wrapperEdge = dropdown.parentElement.getBoundingClientRect().right

		const alignWidthWrapperEdge = wrapperEdge >= threshold - 32

		if (!alignWidthWrapperEdge) {
			const left =
				window.innerWidth - dropdown.getBoundingClientRect().right - 32
			dropdown.setAttribute('style', `left:${left}px;`)
		} else if (alignWidthWrapperEdge) {
			dropdown.setAttribute('style', `left: auto; right: 0;`)
		}
	}

	const toggleDropdown = (
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		attribute: string
	): void => {
		const isOpen = attribute === activeDropdown

		setActiveDropdown(isOpen ? null : attribute)

		if (isOpen || activeDropdown) {
			const dropdown = document.querySelector(
				`[data-dropdown="${activeDropdown}"]`
			) as HTMLElement

			dropdown.setAttribute('style', '')
		}

		if (!matchMedia('(min-width: 48em)').matches) {
			const target = event.target as HTMLElement

			target.setAttribute('style', 'scroll-snap-align: unset;')
			target.scrollIntoView({ behavior: 'smooth' })

			setTimeout(() => {
				target.setAttribute('style', '')
			})
		}
	}

	useEffect(() => {
		if (activeDropdown) {
			positionPanel(activeDropdown)
		}
	}, [activeDropdown])

	const closeOnOutsideClick = useCallback(
		(event: MouseEvent) => {
			if (!(event.target as HTMLElement).closest('[data-dropdowns]')) {
				setActiveDropdown(null)
			}
		},
		[setActiveDropdown]
	)

	const closeOnEscapeKey = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setActiveDropdown(null)
			}
		},
		[setActiveDropdown]
	)

	useEffect(() => {
		// We handle this with a close button on mobile
		if (matchMedia('(min-width: 48em)').matches && activeDropdown !== null) {
			window.addEventListener('mousedown', closeOnOutsideClick)
			window.addEventListener('keydown', closeOnEscapeKey)
		} else {
			window.removeEventListener('mousedown', closeOnOutsideClick)
			window.removeEventListener('keydown', closeOnEscapeKey)
		}
		return () => {
			window.removeEventListener('click', closeOnOutsideClick)
			window.removeEventListener('keydown', closeOnEscapeKey)
		}
	}, [closeOnEscapeKey, closeOnOutsideClick, activeDropdown])

	/**
	 * Mobile-only functionality
	 */
	const [showMobileFilters, setShowMobileFilters] = useState(false)

	const toggleMobileFilters = (): void => {
		if (!showMobileFilters && !activeDropdown) {
			setActiveDropdown('MusicalStyles')
		}

		setShowMobileFilters(!showMobileFilters)
	}
	/**
	 * End of mobile-only functionality
	 */

	return {
		activeDropdown,
		showMobileFilters,
		toggleDropdown,
		toggleMobileFilters,
	}
}
